/* eslint-disable no-nested-ternary */
import { Flex, Text, Tooltip } from '@modusign/design-system';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRaf } from 'react-use';

import LoadingStatus from '@libs/loading-status';

import { useColorScheme } from '@app/hooks/useColorScheme';
import useUsageCount from '@app/hooks/useUsageCount';
import {
  getPermissionsAndLimitsLoadingStatus,
  getUserAuthorityLoadingStatus,
} from '@app/store/user/authority/selectors';

import GNBUsageGauge from './GNBUsageGauge';

export const REMAINING_USAGE_DURATION = 900;

function RemainingSigningRequestCount() {
  const { colorScheme } = useColorScheme();
  const { signingRequestCount } = useUsageCount();
  const userAuthorityLoadingStatus = useSelector(getUserAuthorityLoadingStatus);
  const permissionsAndLimitsLoadingStatus = useSelector(getPermissionsAndLimitsLoadingStatus);

  const isLoading =
    userAuthorityLoadingStatus === LoadingStatus.PENDING ||
    permissionsAndLimitsLoadingStatus === LoadingStatus.PENDING;

  const remainingCountLabel = useMemo(() => {
    if (isLoading) {
      return 0;
    }

    return signingRequestCount.limit - signingRequestCount.usage;
  }, [isLoading, signingRequestCount]);

  const elapsed = useRaf(REMAINING_USAGE_DURATION, 100);

  return (
    <Tooltip content="요금제·사용량 보기" placement="bottom">
      <Flex alignItems="center" as="a" gap="8px" href="/settings/plan">
        <Flex alignItems="center" gap="2px" sx={{ flex: 'none' }}>
          <Text
            color={colorScheme === 'default' ? 'textNeutralUltraLight' : 'textNeutralLight'}
            fontSize="xs"
          >
            잔여
          </Text>
          <Text
            color={
              isLoading
                ? 'textNeutralUltraLight'
                : signingRequestCount.limit - signingRequestCount.usage > 0
                ? colorScheme === 'default'
                  ? 'iconPrimaryBase'
                  : 'iconPrimaryDark'
                : 'pureRed400'
            }
            fontSize="xs"
            fontWeight="bold"
          >
            {signingRequestCount.limit === Infinity ? (
              '무제한'
            ) : (
              <>
                <RemainingCountMonoSpaceNumber width={7}>
                  {Math.floor(remainingCountLabel * elapsed)}
                </RemainingCountMonoSpaceNumber>
                건
              </>
            )}
          </Text>
        </Flex>
        <GNBUsageGauge />
      </Flex>
    </Tooltip>
  );
}

function RemainingCountMonoSpaceNumber({ children, width }: { children: number; width: number }) {
  return (
    <>
      {children
        .toString()
        .split('')
        .map((str, index) => (
          <Text
            display="inline-block"
            fontSize="xs"
            fontWeight="bold"
            // eslint-disable-next-line react/no-array-index-key
            key={`${str}-${index}`}
            sx={{ width }}
          >
            {str}
          </Text>
        ))}
    </>
  );
}

export default RemainingSigningRequestCount;
